import {
  WSButton,
  WSCard,
  WSDivider,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { Transaction, TransactionType } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useState } from "react";
import { useQueryTaxEstimate } from "@hooks/useQueryTaxEstimate";
import { getCurrentIRSYear } from "../../../shared/utils/taxes";

const CalcRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
}> = props => (
  <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
    <WSText.ParagraphSm
      color="gray600"
      weight={props.bold ? "medium" : undefined}
    >
      {props.label}
    </WSText.ParagraphSm>
    <WSText.ParagraphSm weight={props.bold ? "medium" : undefined} tabularNums>
      {props.value}
    </WSText.ParagraphSm>
  </WSFlexBox.CenterY>
);

type Props = { transaction: Transaction };

export const TaxSavings: React.FC<Props> = props => {
  const [showCalc, setShowCalc] = useState(false);
  const queryTaxEstimate = useQueryTaxEstimate(getCurrentIRSYear());

  const isDeductibleExpense =
    props.transaction.type === TransactionType.EXPENSE &&
    props.transaction.isBusiness;

  // The deductible portion: the BE-computed deductibleAmount, else the user-set
  // businessAmount, else the full amount (a business expense with no explicit
  // split is treated as fully deductible).
  const deductible = isDeductibleExpense
    ? Math.abs(
        Number(
          props.transaction.deductibleAmount ??
            props.transaction.businessAmount ??
            props.transaction.amount
        )
      )
    : 0;

  // effectiveTaxRate is a percentage (e.g. 24.5).
  const effectiveTaxRate = queryTaxEstimate.data?.effectiveTaxRate ?? 0;
  const savings = deductible * (effectiveTaxRate / 100);

  if (savings <= 0) {
    return null;
  }

  const money = (value: number) =>
    formatMoney(value, props.transaction.currency);

  return (
    <WSCard>
      <WSList gap="M">
        <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
          <WSText weight="medium">Estimated tax savings</WSText>
          <WSText weight="medium" color="green500" tabularNums>
            {money(savings)}
          </WSText>
        </WSFlexBox.CenterY>

        <WSButton.Link
          type="button"
          rightIcon={showCalc ? "chevron-up" : "chevron-down"}
          onClick={() => setShowCalc(show => !show)}
        >
          How is this calculated?
        </WSButton.Link>

        {showCalc ? (
          <WSList gap="S">
            <CalcRow label="Deductible amount" value={money(deductible)} />
            <CalcRow
              label="Estimated tax rate"
              value={`${effectiveTaxRate.toFixed(2)}%`}
            />
            <WSDivider />
            <CalcRow label="Total savings" value={money(savings)} bold />
            <WSText.ParagraphSm color="gray500">
              Based on your tax profile — your income, work location, and filing
              status. Deducting this expense lowers your taxable income, saving
              roughly this much in taxes.
            </WSText.ParagraphSm>
          </WSList>
        ) : null}
      </WSList>
    </WSCard>
  );
};
